import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { AudioTrackLabel } from '@features/advancedVideoPlayer';
import { DIRECT_CLIP_CUSTOM_FIELD_KEYS } from '../constants';
import {
    BackendDirectClip,
    BackendMediaCustomFieldNames,
    Clip,
    DEFAULT_CLIP,
} from '../types';
import {
    BackendFrameRate,
    BackendMediaObject,
    BackendMediaObjectInstance,
    BackendTrack,
    DEFAULT_FRAME_RATE,
    DEFAULT_MEDIA_IDENTIFIER,
    DEFAULT_MEDIA_IDENTIFIERS,
    FrameRate,
    MediaIdentifier,
    MediaIdentifiers,
    MediaObjectQuality,
    MediaObjectType,
} from '../types/media';

const mapCustomFields = <TTarget, TNames extends string>(
    customFields: Record<TNames, { value?: unknown }>,
    map: Partial<Record<keyof TTarget, TNames>>,
): Partial<TTarget> => {
    return Object.entries(map).reduce((acc, [targetKey, sourceKey]) => {
        return {
            ...acc,
            [targetKey]: customFields[sourceKey as TNames]?.value ?? '',
        };
    }, {} as Partial<TTarget>);
};

const mapFrameRate = (
    backendFrameRate: BackendFrameRate | undefined,
): FrameRate => {
    if (!backendFrameRate) return DEFAULT_FRAME_RATE;

    const { numerator, denumerator, drop_frame } = backendFrameRate;

    return {
        denumerator,
        numerator,
        dropFrames: drop_frame,
        fps: `${numerator}/${denumerator}`,
    };
};

const mapAspectRatio = (
    tracks: BackendTrack[] | undefined,
): { width: number; height: number } => {
    const {
        width = DEFAULT_MEDIA_IDENTIFIER.width,
        height = DEFAULT_MEDIA_IDENTIFIER.height,
    } = tracks?.find((track) => track.type === 'VIDEO') ?? {};

    return { width, height };
};

const mapMediaIdentifier = (
    instance: BackendMediaObjectInstance,
): MediaIdentifier => {
    const { id, mediaObjectId, mimeType, href } = instance;
    const quality = instance.quality as MediaObjectQuality;
    const type = instance.type as MediaObjectType;

    const mediaInfo = instance.mediaInfo;
    const { duration = 0, frames = 0 } = mediaInfo ?? {};

    const frameRate = mapFrameRate(mediaInfo?.sampleRate);
    const { width, height } = mapAspectRatio(mediaInfo?.tracks);

    return {
        ...DEFAULT_MEDIA_IDENTIFIER,
        id,
        mediaObjectId,

        mimeType,
        type,
        quality,
        href,

        duration,
        frames,
        frameRate,
        width,
        height,
    };
};

const mapMediaIdentifiers = (
    instances: BackendMediaObjectInstance[],
): MediaIdentifiers => {
    const mediaIdentifiers: Partial<MediaIdentifiers> = instances.reduce(
        (acc, instance) => {
            const { type, quality, ...rest } = mapMediaIdentifier(instance);

            // const quality = (instance.quality || 'LOW') as MediaObjectQuality;

            if (!acc[type]) {
                acc[type] = {};
            }

            acc[type][quality] = {
                ...rest,
                type,
                quality,
            };

            return acc;
        },
        {} as Partial<MediaIdentifiers>,
    );
    return mergeWithDefault(mediaIdentifiers, DEFAULT_MEDIA_IDENTIFIERS);
};

export const mapDirectClipToFrontend = (raw: BackendDirectClip): Clip => {
    const metadata = raw.clipMetadata || {};
    const mediaObject = raw.mediaObject || ({} as BackendMediaObject);
    const instances = mediaObject.mediaObjectInstances || [];

    const { duration, durationSeconds, name } = mediaObject;

    const mappedCustomFields = mapCustomFields<
        Clip,
        BackendMediaCustomFieldNames
    >(raw.customFields, DIRECT_CLIP_CUSTOM_FIELD_KEYS);

    const audioTracks: AudioTrackLabel[] = [];
    metadata.audioLayout?.groupOfSoundfieldGroups?.[0]?.soundFieldGroups?.forEach(
        (group) => {
            group.audioChannels?.forEach((ch) => {
                audioTracks.push({
                    label: ch.TagSymbol,
                    tagName: ch.TagName,
                    groupName: group.TagName,
                    flowNativeAudioChannel: ch.flowNativeAudioChannel,
                });
            });
        },
    );

    const frameRate = mapFrameRate(mediaObject.sampleRate);
    const mediaIdentifiers = mapMediaIdentifiers(instances);

    const res: Clip = mergeWithDefault(
        {
            // ...DEFAULT_CLIP,
            ...mappedCustomFields,

            id: raw.id,
            mediaObjectId: raw.mediaObjectId,

            // Metadata
            clipName: metadata.cardName || name,
            audioMapping: metadata.audioLayout?.templateLabel || '',
            created: raw.created ? new Date(raw.created).toISOString() : '',

            // Technical Context
            mediaIdentifiers,

            audioTracks,
            frameRate,

            durationSeconds,
            duration,
        },
        DEFAULT_CLIP,
    );

    return res;
};

export const mapDirectClipMediaToFrontend = (
    raw: BackendMediaObjectInstance,
): MediaIdentifier => {
    return mapMediaIdentifier(raw);
};
